import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Logo from "../assets/images/cicoplogo.png";
import { useApp } from "../context/AppContext";

import {
  FiHome, FiUsers, FiFileText, FiCreditCard, FiClipboard,
  FiBriefcase, FiSettings, FiChevronRight, FiChevronDown,
  FiAlertTriangle, FiCalendar, FiDollarSign, FiList,
  FiCheckSquare, FiBarChart2, FiAward, FiBookOpen, FiPlusCircle,
  FiClock, FiShield, FiImage,
  FiLayers, FiGrid, FiTrendingUp, FiTruck, FiSend,
  FiActivity, FiCheckCircle
} from "react-icons/fi";


// ─── NavItem ──────────────────────────────────────────────────────────────────
const NavItem = ({ to, icon: Icon, label, end = false, subItem = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `${subItem ? "nav-link nav-sublink" : "nav-link"} ${isActive ? "active" : ""}`
    }
  >
    {Icon
      ? <Icon size={subItem ? 13 : 15} style={{ flexShrink: 0 }} />
      : <FiPlusCircle size={13} style={{ flexShrink: 0 }} />}
    {label}
  </NavLink>
);

// ─── NavGroup ─────────────────────────────────────────────────────────────────
const NavGroup = ({ icon: Icon, label, isOpen, isActive, onToggle, children }) => (
  <div className={`nav-group ${isActive ? "active" : ""}`}>
    <button
      type="button"
      className={`nav-group-trigger ${isActive ? "active" : ""}`}
      onClick={onToggle}
    >
      <span style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
        <Icon size={15} style={{ flexShrink: 0 }} />
        {label}
      </span>
      {isOpen
        ? <FiChevronDown size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
        : <FiChevronRight size={13} style={{ flexShrink: 0, opacity: 0.7 }} />}
    </button>
    {isOpen && <div className="nav-submenu">{children}</div>}
  </div>
);
// ─── Sidebar ──────────────────────────────────────────────────────────────────
export default function Sidebar({ className = "" }) {
  const location = useLocation();
  const { user, collegeName: appCollegeName, logoSrc: appLogoSrc } = useApp();
  const isSuperAdmin = user?.role === "superadmin";
  const collegeName = appCollegeName || "Central India College of Pharmacy";
  const logoSrc = appLogoSrc || Logo;

  const [studentsOpen, setStudentsOpen] = useState(location.pathname.startsWith("/students"));
  const [certificatesOpen, setCertificatesOpen] = useState(location.pathname.startsWith("/bonafide") || location.pathname.startsWith("/leaving"));
  const [feesOpen, setFeesOpen] = useState(location.pathname.startsWith("/fees"));
  const [attendanceOpen, setAttendanceOpen] = useState(location.pathname.startsWith("/attendance"));
  const [employeeOpen, setEmployeeOpen] = useState(location.pathname.startsWith("/employees") || location.pathname.startsWith("/leave") || location.pathname.startsWith("/salary") || location.pathname.startsWith("/payroll"));
  const [examOpen, setExamOpen] = useState(location.pathname.startsWith("/exams"));
  const [inventoryOpen, setInventoryOpen] = useState(location.pathname.startsWith("/inventory"));
  const [libraryOpen, setLibraryOpen] = useState(location.pathname.startsWith("/library"));
  const [eventsOpen, setEventsOpen] = useState(location.pathname.startsWith("/events"));
  const [homeworkOpen, setHomeworkOpen] = useState(location.pathname.startsWith("/homework"));
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith("/settings"));
  const [hostelOpen, setHostelOpen] = useState(
    location.pathname.startsWith("/hostel"),
  );
  const [escalationsOpen, setEscalationsOpen] = useState(location.pathname.startsWith("/escalations"));

  useEffect(() => {
    if (location.pathname.startsWith("/students")) setStudentsOpen(true);
    if (location.pathname.startsWith("/bonafide") || location.pathname.startsWith("/leaving")) setCertificatesOpen(true);
    if (location.pathname.startsWith("/fees")) setFeesOpen(true);
    if (location.pathname.startsWith("/attendance")) setAttendanceOpen(true);
    if (location.pathname.startsWith("/employees") || location.pathname.startsWith("/leave") || location.pathname.startsWith("/salary") || location.pathname.startsWith("/payroll")) setEmployeeOpen(true);
    if (location.pathname.startsWith("/exams")) setExamOpen(true);
    if (location.pathname.startsWith("/inventory")) setInventoryOpen(true);
    if (location.pathname.startsWith("/library")) setLibraryOpen(true);
    if (location.pathname.startsWith("/events")) setEventsOpen(true);
    if (location.pathname.startsWith("/homework")) setHomeworkOpen(true);
    if (location.pathname.startsWith("/settings")) setSettingsOpen(true);
    if (location.pathname.startsWith("/hostel")) setHostelOpen(true);
    if (location.pathname.startsWith("/escalations")) setEscalationsOpen(true);
  }, [location.pathname]);

  return (
    <aside className={`sidebar ${className}`}>
      {/* ── Logo ── */}
      <div className="sidebar-logo" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "8px", padding: "1.2rem 1rem" }}>
        <div style={{
          width: 128,
          height: 128,
          borderRadius: "12px",
          background: "rgba(255, 255, 255, 0.08)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px",
          overflow: "hidden"
        }}>
          <img
            src={logoSrc}
            alt={collegeName}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        <div>
          <h2 style={{ fontSize: "13.5px", fontWeight: 700, color: "#f8fafc", lineHeight: 1.25, margin: 0 }}>
            {collegeName}
          </h2>
          {/*           {sansthaName && (
            <span style={{ fontSize: "10px", color: "#94a3b8", display: "block", marginTop: "4px", lineHeight: 1.2 }}>
              {sansthaName}
            </span>
          )} */}
        </div>
      </div>

      {/* ── Navigation ── */}
      <nav className="sidebar-nav" style={{ flex: 1 }}>
        {isSuperAdmin && (
          <>
            <NavItem to="/superadmin" icon={FiHome} label="SuperAdmin Hub" />
            <NavItem to="/superadmin/cash-radar" icon={FiActivity} label="Campus Cash Radar" />
            <NavItem to="/colleges" icon={FiGrid} label="Manage Colleges" />
          </>
        )}
        {!isSuperAdmin && <NavItem to="/" icon={FiHome} label="Dashboard" end />}
        <NavItem to="/communications" icon={FiSend} label="Communication" />
        <NavItem to="/users" icon={FiShield} label="User Accounts" />

        <NavGroup icon={FiLayers} label="Escalations & Requests" isOpen={escalationsOpen}
          isActive={location.pathname.startsWith("/escalations")}
          onToggle={() => setEscalationsOpen((p) => !p)}>
          <NavItem to="/escalations" icon={FiList} label="All Requests" end subItem />
          {!isSuperAdmin && (
            <NavItem to="/escalations/new" icon={FiPlusCircle} label="New Request" subItem />
          )}
        </NavGroup>

        <NavGroup icon={FiUsers} label="Students" isOpen={studentsOpen}
          isActive={location.pathname.startsWith("/students") || location.pathname.startsWith("/subjects") || location.pathname.startsWith("/timetables")}
          onToggle={() => setStudentsOpen((p) => !p)}>
          <NavItem to="/students" icon={FiList} label="All Students" subItem />
          <NavItem to="/timetables" icon={FiClock} label="Timetables" subItem />
          <NavItem to="/subjects" icon={FiBookOpen} label="Subjects & Syllabus" end subItem />
          <NavItem to="/subjects/performance" icon={FiTrendingUp} label="Faculty Syllabus & Tracking" subItem />
          <NavItem to="/students/promote" icon={FiCheckSquare} label="Promote Students" subItem />
        </NavGroup>


        <NavGroup icon={FiBookOpen} label="Exams & Results" isOpen={examOpen}
          isActive={location.pathname.startsWith("/exams")}
          onToggle={() => setExamOpen((p) => !p)}>
          <NavItem to="/exams" icon={FiList} label="All Exams" subItem />
          <NavItem to="/exams/new" icon={FiPlusCircle} label="Create Exam" subItem />
        </NavGroup>

        {/*         <NavGroup icon={FiEdit3} label="Homework" isOpen={homeworkOpen}
          isActive={location.pathname.startsWith("/homework")}
          onToggle={() => setHomeworkOpen((p) => !p)}>
          <NavItem to="/homework" icon={FiList} label="All Homework" end subItem />
          <NavItem to="/homework/new" icon={FiPlusCircle} label="Assign Homework" subItem />
        </NavGroup> */}

        {/*         <NavGroup icon={FiFileText} label="Certificates" isOpen={certificatesOpen}
          isActive={location.pathname.startsWith("/bonafide") || location.pathname.startsWith("/leaving")}
          onToggle={() => setCertificatesOpen((p) => !p)}>
          <NavItem to="/bonafide" icon={FiAward} label="Bonafide" subItem />
          <NavItem to="/leaving" icon={FiBookOpen} label="Leaving (TC)" subItem />
        </NavGroup> */}

        <NavGroup icon={FiCreditCard} label="Fee Management" isOpen={feesOpen}
          isActive={location.pathname.startsWith("/fees") || location.pathname.startsWith("/fines") || location.pathname.startsWith("/transport")}
          onToggle={() => setFeesOpen((p) => !p)}>
          <NavItem to="/fees" icon={FiBarChart2} label="Fee Dashboard" end subItem />
          <NavItem to="/fees/receipts" icon={FiList} label="All Receipts" subItem />
          {/* <NavItem to="/fees/new" icon={FiPlusCircle} label="Add Receipt" subItem /> */}
          <NavItem to="/fees/pending" icon={FiAlertTriangle} label="Pending Fees" subItem />
          <NavItem to="/fines" icon={FiShield} label="Charges & Fines" subItem />
          <NavItem to="/transport" icon={FiTruck} label="Bus / Transport" subItem />
          <NavItem to="/fees/reconciliation" icon={FiCheckCircle} label="Cash & Bank Audit" subItem />
          <NavItem to="/settings/fee-structures" icon={FiLayers} label="Fee Structures" subItem />
        </NavGroup>
        <NavItem to="/scholarships" icon={FiAward} label="Scholarships" />

        <NavGroup icon={FiClipboard} label="Attendance" isOpen={attendanceOpen}
          isActive={location.pathname.startsWith("/attendance")}
          onToggle={() => setAttendanceOpen((p) => !p)}>
          <NavItem to="/attendance/mark" icon={FiCheckSquare} label="Mark Attendance" subItem />
          <NavItem to="/attendance/report" icon={FiBarChart2} label="Attendance Report" subItem />
          <NavItem to="/attendance/subject-report" icon={FiLayers} label="Subject-wise Report" subItem />
        </NavGroup>

        <NavGroup icon={FiBriefcase} label="Employees" isOpen={employeeOpen}
          isActive={location.pathname.startsWith("/employees") || location.pathname.startsWith("/leave") || location.pathname.startsWith("/salary") || location.pathname.startsWith("/payroll")}
          onToggle={() => setEmployeeOpen((p) => !p)}>
          <NavItem to="/employees" icon={FiList} label="Employees List" subItem />
          <NavItem to="/employees/attendance/add" icon={FiCheckSquare} label="Add Attendance" subItem />
          <NavItem to="/employees/attendance/report" icon={FiBarChart2} label="Attendance Report" subItem />
          <NavItem to="/leave-applications" icon={FiCalendar} label="Leave Applications" subItem />
          <NavItem to="/leave-types" icon={FiClock} label="Leave Types" subItem />
          <NavItem to="/salary-master" icon={FiDollarSign} label="Salary Structures" subItem />
          <NavItem to="/payroll" icon={FiDollarSign} label="Payroll" subItem />
        </NavGroup>

        {/*         <NavGroup icon={FiPackage} label="Inventory" isOpen={inventoryOpen}
          isActive={location.pathname.startsWith("/inventory")}
          onToggle={() => setInventoryOpen((p) => !p)}>
          <NavItem to="/inventory" icon={FiBarChart2} label="Dashboard" subItem />
          <NavItem to="/inventory/items" icon={FiList} label="Items" subItem />
          <NavItem to="/inventory/transactions" icon={FiClock} label="Transactions" subItem />
          <NavItem to="/inventory/suppliers" icon={FiBriefcase} label="Suppliers" subItem />
          <NavItem to="/inventory/categories" icon={FiTag} label="Categories" subItem />
        </NavGroup> */}

        {/*         <NavGroup icon={FiBook} label="Library" isOpen={libraryOpen}
          isActive={location.pathname.startsWith("/library")}
          onToggle={() => setLibraryOpen((p) => !p)}>
          <NavItem to="/library/books" icon={FiList} label="All Books" subItem />
          <NavItem to="/library/fines" icon={FiDollarSign} label="Pending Fines" subItem />
          <NavItem to="/library/settings" icon={FiSettings} label="Library Settings" subItem />
        </NavGroup> */}
        <NavGroup
          icon={FiGrid}
          label="Hostel Management"
          isOpen={hostelOpen}
          isActive={location.pathname.startsWith("/hostel")}
          onToggle={() => setHostelOpen((p) => !p)}
        >
          <NavItem to="/hostels" icon={FiList} label="All Hostels" end subItem />
          <NavItem to="/hostel-allocations" icon={FiUsers} label="Allocations" subItem />
          <NavItem to="/hostel-fee-structures" icon={FiLayers} label="Fee Structures" subItem />
          <NavItem to="/hostel-fee-collect" icon={FiDollarSign} label="Collect Fee" subItem />
          <NavItem to="/hostel-fee-receipts" icon={FiFileText} label="Fee Receipts" subItem />
          <NavItem to="/hostel-fees/pending" icon={FiAlertTriangle} label="Pending Fees" subItem />
        </NavGroup>

        <NavGroup icon={FiCalendar} label="Events & Gallery" isOpen={eventsOpen}
          isActive={location.pathname.startsWith("/events")}
          onToggle={() => setEventsOpen((p) => !p)}>
          <NavItem to="/events" icon={FiCalendar} label="Events" subItem />
          <NavItem to="/gallery" icon={FiImage} label="Gallery" subItem />
        </NavGroup>

        <NavGroup icon={FiSettings} label="Settings" isOpen={settingsOpen}
          isActive={location.pathname.startsWith("/settings")}
          onToggle={() => setSettingsOpen((p) => !p)}>
          <NavItem to="/settings" icon={FiSettings} label="Settings" subItem />
          <NavItem to="/settings/courses" icon={FiBookOpen} label="Courses" subItem />
        </NavGroup>
      </nav>
    </aside>
  );
}
